// ─── Org chart image export ───────────────────────────────────────────────────
//
// Draws the reporting tree onto a canvas as a PNG for a slide deck (the
// onboarding presentation was the ask). It is drawn from the data rather than
// screenshotted from the page: the on-screen chart is a pan/zoom canvas with
// controls, hints and a selection on it, and its text is sized for a screen
// at 9px, which is unreadable once a slide is projected.
//
// The layout is the same slot arithmetic as the page (a card's slot is its
// visible leaves times the slot width, centred over its children), so what is
// open on screen is what comes out. `layoutOrgChart` and `exportScale` are pure
// and tested in tests/org-chart-export.test.mjs; the drawing needs a browser.

import { visibleLeaves } from './orgTree.js';

export const EXPORT_DIMS = {
  SLOT_W: 200,
  CARD_W: 176,
  CARD_H: 160,
  LEVEL_GAP: 56,
  ROOT_GAP: 48,
  PAD: 48,
  AVATAR: 64,
  HEADER_H: 84,
  LEGEND_H: 44,
};

const NAVY  = '#0D1D32';
const GOLD  = '#A1723D';
const GREY5 = '#6B7280';
const GREY4 = '#9CA3AF';
const LINE  = '#D1D5DB';
const BORDER = '#E5E7EB';
const FONT  = "Montserrat, Arial, sans-serif";
const LEGEND_ROW_H = 20;
const TAB_GAP = 6;

/**
 * Card positions and connector lines for a forest, in chart coordinates with
 * the origin at the top-left of the first card's row.
 *
 * Returns `{ cards, lines, width, height }`. Each card is `{ node, x, y,
 * hiddenReports }`, where `hiddenReports` is the number of direct reports not
 * drawn because the branch is shut (0 when open or a leaf). Lines are
 * `{ x1, y1, x2, y2 }`, each one horizontal or vertical.
 */
export function layoutOrgChart(roots, isCollapsed = () => false, dims = EXPORT_DIMS) {
  const { SLOT_W, CARD_W, CARD_H, LEVEL_GAP, ROOT_GAP } = dims;
  const cards = [];
  const lines = [];
  let maxDepth = -1;

  const place = (node, left, depth) => {
    maxDepth = Math.max(maxDepth, depth);
    const slotW = visibleLeaves(node, isCollapsed) * SLOT_W;
    const cx = left + slotW / 2;
    const y = depth * (CARD_H + LEVEL_GAP);
    const shut = node.children.length > 0 && isCollapsed(node.id);
    cards.push({ node, x: cx - CARD_W / 2, y, hiddenReports: shut ? node.children.length : 0 });
    if (node.children.length === 0 || shut) return;

    const bottom = y + CARD_H;
    const midY = bottom + LEVEL_GAP / 2;
    const childTop = bottom + LEVEL_GAP;
    lines.push({ x1: cx, y1: bottom, x2: cx, y2: midY });
    let childLeft = left;
    const centres = [];
    for (const child of node.children) {
      const w = visibleLeaves(child, isCollapsed) * SLOT_W;
      const ccx = childLeft + w / 2;
      centres.push(ccx);
      lines.push({ x1: ccx, y1: midY, x2: ccx, y2: childTop });
      place(child, childLeft, depth + 1);
      childLeft += w;
    }
    if (centres.length > 1) {
      lines.push({ x1: centres[0], y1: midY, x2: centres[centres.length - 1], y2: midY });
    }
  };

  let left = 0;
  roots.forEach((root, i) => {
    if (i > 0) left += ROOT_GAP;
    place(root, left, 0);
    left += visibleLeaves(root, isCollapsed) * SLOT_W;
  });

  // The slot is wider than the card, so trim the half-gap either side that
  // would otherwise sit as dead margin on the outer edges.
  const inset = (SLOT_W - CARD_W) / 2;
  if (cards.length) {
    cards.forEach(c => { c.x -= inset; });
    lines.forEach(l => { l.x1 -= inset; l.x2 -= inset; });
  }
  return {
    cards,
    lines,
    width: cards.length ? left - inset * 2 : 0,
    height: maxDepth < 0 ? 0 : (maxDepth + 1) * CARD_H + maxDepth * LEVEL_GAP,
  };
}

/**
 * Pixel ratio for the PNG: as sharp as 3x for a projector, but held inside
 * what a browser canvas will allocate. Past roughly 16k pixels a side, or a
 * total area in the low hundreds of millions, Chrome and Safari hand back a
 * blank canvas without an error, which would download as an empty image.
 */
export function exportScale(width, height, { max = 3, maxSide = 16000, maxArea = 120e6 } = {}) {
  if (!(width > 0) || !(height > 0)) return 1;
  return Math.max(0.25, Math.min(
    max,
    maxSide / width,
    maxSide / height,
    Math.sqrt(maxArea / (width * height)),
  ));
}

/**
 * Break `text` into at most `maxLines` lines no wider than `maxWidth`, by
 * `measure(str) -> px`. The last line is ellipsised if text is left over.
 */
export function wrapText(text, maxWidth, maxLines, measure) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  let i = 0;
  for (; i < words.length; i++) {
    const next = line ? `${line} ${words[i]}` : words[i];
    if (measure(next) <= maxWidth || !line) { line = next; continue; }
    if (lines.length === maxLines - 1) break;
    lines.push(line);
    line = words[i];
  }
  if (line) lines.push(line);
  if (!lines.length) return lines;
  // Whatever did not fit is cut a character at a time from the full remainder,
  // so "Christopher Montgomery" ends "Christopher Montgo…" rather than
  // dropping the surname whole.
  let last = [lines[lines.length - 1], ...words.slice(i)].join(' ');
  if (measure(last) <= maxWidth) return lines;
  while (last.length > 1 && measure(`${last}…`) > maxWidth) last = last.slice(0, -1).trimEnd();
  lines[lines.length - 1] = `${last}…`;
  return lines;
}

const initialsOf = p => `${p.first_name?.[0] || ''}${p.last_name?.[0] || ''}`.toUpperCase();

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  if (ctx.roundRect) { ctx.roundRect(x, y, w, h, r); return; }
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    // A photo that will not decode falls back to initials rather than failing
    // the whole export.
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function drawCard(ctx, card, photo, teamColor) {
  const { CARD_W, CARD_H, AVATAR } = EXPORT_DIMS;
  const { node, x, y, hiddenReports } = card;

  ctx.save();
  ctx.shadowColor = 'rgba(13,29,50,0.08)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 2;
  roundRect(ctx, x, y, CARD_W, CARD_H, 12);
  ctx.fillStyle = '#FFFFFF';
  ctx.fill();
  ctx.restore();
  roundRect(ctx, x, y, CARD_W, CARD_H, 12);
  ctx.strokeStyle = BORDER;
  ctx.lineWidth = 1;
  ctx.stroke();

  // Team colour along the top edge, as on the team board.
  if (teamColor) {
    ctx.save();
    roundRect(ctx, x, y, CARD_W, CARD_H, 12);
    ctx.clip();
    ctx.fillStyle = teamColor;
    ctx.fillRect(x, y, CARD_W, 4);
    ctx.restore();
  }

  const cx = x + CARD_W / 2;
  const ay = y + 16;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, ay + AVATAR / 2, AVATAR / 2, 0, Math.PI * 2);
  ctx.clip();
  if (photo) {
    // object-fit: cover
    const s = Math.max(AVATAR / photo.width, AVATAR / photo.height);
    const w = photo.width * s, h = photo.height * s;
    ctx.drawImage(photo, cx - w / 2, ay + (AVATAR - h) / 2, w, h);
  } else {
    ctx.fillStyle = node.avatar_color || GOLD;
    ctx.fillRect(cx - AVATAR / 2, ay, AVATAR, AVATAR);
    ctx.fillStyle = '#FFFFFF';
    ctx.font = `700 ${Math.floor(AVATAR * 0.32)}px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initialsOf(node), cx, ay + AVATAR / 2 + 1);
  }
  ctx.restore();
  ctx.beginPath();
  ctx.arc(cx, ay + AVATAR / 2, AVATAR / 2, 0, Math.PI * 2);
  ctx.strokeStyle = BORDER;
  ctx.lineWidth = 2;
  ctx.stroke();

  const textW = CARD_W - 20;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  let ty = ay + AVATAR + 22;

  // A long name steps down a size or two before it is cut.
  ctx.fillStyle = NAVY;
  const fullName = `${node.first_name || ''} ${node.last_name || ''}`.trim();
  let size = 14;
  ctx.font = `700 ${size}px ${FONT}`;
  while (size > 11 && ctx.measureText(fullName).width > textW) ctx.font = `700 ${--size}px ${FONT}`;
  const [name] = wrapText(fullName, textW, 1, s => ctx.measureText(s).width);
  if (name) ctx.fillText(name, cx, ty);
  ty += 17;

  if (node.designation) {
    ctx.fillStyle = GREY5;
    ctx.font = `500 11px ${FONT}`;
    for (const l of wrapText(node.designation, textW, 2, s => ctx.measureText(s).width)) {
      ctx.fillText(l, cx, ty);
      ty += 14;
    }
  }
  if (node.team_name) {
    ctx.fillStyle = GREY4;
    ctx.font = `500 10px ${FONT}`;
    const [team] = wrapText(node.team_name, textW, 1, s => ctx.measureText(s).width);
    ctx.fillText(team, cx, y + CARD_H - 12);
  }

  // A shut branch says so on a tab under the card, in the gap its connector
  // would have used, rather than the card passing for somebody with no reports.
  if (hiddenReports > 0) {
    const label = `+${hiddenReports} report${hiddenReports === 1 ? '' : 's'}`;
    ctx.font = `700 10px ${FONT}`;
    const w = ctx.measureText(label).width + 16;
    roundRect(ctx, cx - w / 2, y + CARD_H + TAB_GAP, w, 18, 9);
    ctx.fillStyle = '#F3F4F6';
    ctx.fill();
    ctx.strokeStyle = BORDER;
    ctx.stroke();
    ctx.fillStyle = GREY5;
    ctx.textBaseline = 'middle';
    ctx.fillText(label, cx, y + CARD_H + TAB_GAP + 9.5);
  }
}

/**
 * Render the chart to a PNG blob.
 *
 * `options`: `{ title, subtitle, legend, background }`. `legend` keys the
 * teams on the drawn cards; `background` is a colour, or null for transparent.
 * `teamColorOf(node)` gives a card's top stripe. Resolves `{ blob, width,
 * height, people }`, the size in pixels and the number of cards drawn.
 */
export async function renderOrgChartPng(roots, isCollapsed, teamColorOf, options = {}) {
  const { PAD, HEADER_H, LEGEND_H } = EXPORT_DIMS;
  const { title, subtitle, legend: withLegend, background = '#FFFFFF' } = options;
  const layout = layoutOrgChart(roots, isCollapsed);
  const legend = withLegend
    ? [...new Set(layout.cards.map(c => c.node.team_name).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b))
        .map(name => ({ name, color: teamColorOf({ team_name: name }) }))
        .filter(item => item.color)
    : [];

  // Canvas text uses whatever is loaded at the moment of drawing, and
  // Montserrat arrives as a web font, so ask for the weights in use first.
  if (document.fonts?.load) {
    await Promise.all(['500 11px', '700 14px', '800 28px'].map(f => document.fonts.load(`${f} Montserrat`).catch(() => null)));
  }
  const photos = new Map(await Promise.all(
    layout.cards
      .filter(c => c.node.profile_photo)
      .map(async c => [c.node.id, await loadImage(c.node.profile_photo)]),
  ));

  const headerH = title ? HEADER_H : 0;
  // Wide enough for the title even over a one-card chart.
  const width = Math.max(layout.width, title ? 520 : 0) + PAD * 2;
  // The key wraps onto as many rows as it needs rather than losing teams off
  // the end of a narrow chart.
  const measureCtx = document.createElement('canvas').getContext('2d');
  measureCtx.font = `500 11px ${FONT}`;
  const legendRows = [];
  let row = [], rowW = 0;
  for (const item of legend) {
    const w = 14 + measureCtx.measureText(item.name).width;
    if (row.length && rowW + w > width - PAD * 2) { legendRows.push(row); row = []; rowW = 0; }
    row.push({ ...item, x: rowW });
    rowW += w + 22;
  }
  if (row.length) legendRows.push(row);
  const legendH = legendRows.length ? LEGEND_H + (legendRows.length - 1) * LEGEND_ROW_H : 0;
  // Room for a "+N reports" tab under the lowest row.
  const height = headerH + layout.height + TAB_GAP + 18 + legendH + PAD * 2;
  const scale = exportScale(width, height);

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, width, height);
  }

  if (title) {
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = NAVY;
    ctx.font = `800 28px ${FONT}`;
    ctx.fillText(title, PAD, PAD + 26);
    ctx.fillStyle = GOLD;
    ctx.fillRect(PAD, PAD + 40, 64, 3);
    if (subtitle) {
      ctx.fillStyle = GREY5;
      ctx.font = `500 13px ${FONT}`;
      ctx.fillText(subtitle, PAD, PAD + 64);
    }
  }

  const ox = (width - layout.width) / 2;
  const oy = PAD + headerH;
  ctx.save();
  ctx.translate(ox, oy);
  ctx.strokeStyle = LINE;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'square';
  ctx.beginPath();
  for (const l of layout.lines) { ctx.moveTo(l.x1, l.y1); ctx.lineTo(l.x2, l.y2); }
  ctx.stroke();
  for (const card of layout.cards) drawCard(ctx, card, photos.get(card.node.id), teamColorOf(card.node));
  ctx.restore();

  ctx.font = `500 11px ${FONT}`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  legendRows.forEach((items, r) => {
    const ly = height - PAD - legendH + 24 + r * LEGEND_ROW_H;
    for (const item of items) {
      ctx.fillStyle = item.color;
      ctx.beginPath();
      ctx.arc(PAD + item.x + 5, ly, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = GREY5;
      ctx.fillText(item.name, PAD + item.x + 14, ly);
    }
  });

  const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
  return { blob, width: canvas.width, height: canvas.height, people: layout.cards.length };
}
